/**
 * data-sources-lib.ts — Logic thuần cho khối "Nguồn dữ liệu bán hàng" ở màn Train AI.
 * API: GET /ai/data-sources (chỉ nguồn ĐANG HOẠT ĐỘNG) · cấu hình lưu trong bot.toolsJson:
 *   { dataSources?: { products: string[]; orders: string[] }, inStockOnly?: boolean }
 * Bot cũ chưa có dataSources = dùng mọi nguồn sản phẩm đang hoạt động, chưa tra đơn.
 */
import { currentLocale, i18n } from '@/lib/i18n'
import type { AiBotToolsJson, AiDataSource } from '@/hooks/use-ai'

export type SourceKind = 'products' | 'orders'

export interface SourceSelection {
  products: string[]
  orders: string[]
  /** true = bot chưa từng chọn (đang dùng mặc định). */
  isDefault: boolean
}

/** Lựa chọn hiện hành của bot (mặc định: mọi nguồn sản phẩm, không nguồn đơn). */
export function currentSelection(tools: AiBotToolsJson | null | undefined, sources: AiDataSource[]): SourceSelection {
  const ds = tools?.dataSources
  if (!ds) {
    return { products: sources.filter((s) => s.products).map((s) => s.key), orders: [], isDefault: true }
  }
  return { products: [...ds.products], orders: [...ds.orders], isDefault: false }
}

/** Tick / bỏ tick một nguồn → toolsJson mới (search_products bật khi còn ≥1 nguồn sản phẩm). */
export function toggleSource(
  tools: AiBotToolsJson | null | undefined,
  sources: AiDataSource[],
  kind: SourceKind,
  key: string,
  on: boolean,
): AiBotToolsJson {
  const sel = currentSelection(tools, sources)
  const list = sel[kind].filter((k) => k !== key)
  if (on) list.push(key)
  const next: AiBotToolsJson = { ...(tools ?? {}), dataSources: { products: sel.products, orders: sel.orders, [kind]: list } }
  if (kind === 'products') {
    const sp = tools?.search_products ?? { enabled: true, guardrail: { categoryIds: [] } }
    next.search_products = { ...sp, enabled: list.length > 0 }
  }
  return next
}

export function setInStockOnly(tools: AiBotToolsJson | null | undefined, on: boolean): AiBotToolsJson {
  return { ...(tools ?? {}), inStockOnly: on }
}

/** Nguồn bot đã chọn nhưng nay không còn dữ liệu / tích hợp tắt (AI tự bỏ qua). */
export function staleSources(tools: AiBotToolsJson | null | undefined, sources: AiDataSource[]): Array<{ key: string; kind: SourceKind }> {
  const ds = tools?.dataSources
  if (!ds) return []
  const has = (k: string, kind: SourceKind) => sources.some((s) => s.key === k && s[kind])
  return [
    ...ds.products.filter((k) => !has(k, 'products')).map((key) => ({ key, kind: 'products' as const })),
    ...ds.orders.filter((k) => !has(k, 'orders')).map((key) => ({ key, kind: 'orders' as const })),
  ]
}

/**
 * Cách hiển thị khối: 'loading' (chưa tải), 'hint' (không có nguồn nào → ẩn khối, chỉ một dòng gợi ý
 * dẫn tới Tích hợp), 'list' (có nguồn).
 */
export function salesSourcesMode(sources: AiDataSource[] | undefined): 'loading' | 'hint' | 'list' {
  if (!sources) return 'loading'
  return sources.length === 0 ? 'hint' : 'list'
}

/** "12.345" — số đếm gọn. */
export function fmtCount(n: number): string {
  return n.toLocaleString(currentLocale())
}

/** Dòng số liệu của một nguồn, vd "18.409 sản phẩm · 5 còn hàng · 14 đơn". */
export function sourceStats(s: AiDataSource): string {
  const parts: string[] = []
  if (s.products) {
    parts.push(i18n.t('aiOps:dataSourcesLib.products', { n: fmtCount(s.products.active) }))
    if (s.products.stockTracked > 0) parts.push(i18n.t('aiOps:dataSourcesLib.inStock', { n: fmtCount(s.products.inStock) }))
  }
  if (s.orders) parts.push(i18n.t('aiOps:dataSourcesLib.orders', { n: fmtCount(s.orders.total) }))
  return parts.join(' · ')
}

/** "đồng bộ lúc 00:30 01/10" (giờ Việt Nam) hoặc null. */
export function syncedAtLabel(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const mins = Math.round((now.getTime() - d.getTime()) / 60000)
  if (mins >= 0 && mins < 1) return i18n.t('aiOps:dataSourcesLib.syncedJustNow')
  if (mins >= 0 && mins < 60) return i18n.t('aiOps:dataSourcesLib.syncedMinutesAgo', { count: mins })
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', hourCycle: 'h23',
  }).formatToParts(d)
  const v = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return i18n.t('aiOps:dataSourcesLib.syncedAt', { time: `${v('hour')}:${v('minute')}`, date: `${v('day')}/${v('month')}` })
}

/** Tóm tắt trace chat thử: "Evotech · 3 sản phẩm" từ các bước tool search_products / lookup_orders. */
export function traceSourceSummary(
  trace: Array<{ step: string; payload: unknown }> | undefined,
  labelOf: (key: string) => string,
): string | null {
  if (!trace?.length) return null
  const prod = new Map<string, number>()
  const orders = new Map<string, number>()
  for (const t of trace) {
    if (t.step !== 'tool') continue
    const p = t.payload as { ten_cong_cu?: string; ket_qua_diem?: Array<{ source?: string }> } | null
    const target = p?.ten_cong_cu === 'search_products' ? prod : p?.ten_cong_cu === 'lookup_orders' ? orders : null
    if (!target) continue
    for (const h of p?.ket_qua_diem ?? []) {
      if (h.source) target.set(h.source, (target.get(h.source) ?? 0) + 1)
    }
  }
  const parts = [
    ...[...prod].map(([k, n]) => i18n.t('aiOps:dataSourcesLib.traceProducts', { label: labelOf(k), count: n })),
    ...[...orders].map(([k, n]) => i18n.t('aiOps:dataSourcesLib.traceOrders', { label: labelOf(k), count: n })),
  ]
  return parts.length ? parts.join(', ') : null
}
